import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import cx from 'classnames';
import _noop from 'lodash/noop';
import _map from 'lodash/map';
import _curry from 'lodash/curry';
import _forEach from 'lodash/forEach';
import _omit from 'lodash/omit';
import _last from 'lodash/last';
import _indexOf from 'lodash/indexOf';
import _size from 'lodash/size';
import _isNil from 'lodash/isNil';

// Constants
import { EMPTY_OBJECT, EMPTY_ARRAY } from '@tekion/tekion-base/app.constants';

// Helpers
import { isRowNotEmpty } from '@tekion/tekion-base/helpers/tableRowHelper';
import isStringEmpty from '@tekion/tekion-base/utils/isStringEmpty';

// Builders
import CellConfig from '../../builders/CellConfig';
import ColumnConfig from '../../builders/ColumnConfig';
var DEFAULT_COLUMN_CONFIG = new ColumnConfig();
var DEFAULT_CELL_CONFIG = new CellConfig();
var makeClassName = function makeClassName(rowIndex, columnIndex) {
  return "focusable focusClass-indexed-".concat(rowIndex, ".").concat(columnIndex, "-indexed-");
};
var getCellProps = function getCellProps() {
  var cellConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : DEFAULT_CELL_CONFIG;
  var props = arguments.length > 1 ? arguments[1] : undefined;
  var columnMapCellPropsToComponentProps = cellConfig.getMapCellPropsToComponentProps() || _noop;
  var cellProps = columnMapCellPropsToComponentProps(props) || EMPTY_OBJECT;
  return _objectSpread(_objectSpread({}, cellProps), {}, {
    originalClassName: 'originalClassName' in cellProps ? cellProps.originalClassName : cellProps.className
  });
};
var mapCellPropsToComponentProps = function mapCellPropsToComponentProps(_ref) {
  var index = _ref.index,
    _ref$cellConfig = _ref.cellConfig,
    cellConfig = _ref$cellConfig === void 0 ? DEFAULT_CELL_CONFIG : _ref$cellConfig;
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
    var rowIndex = props.rowIndex;
    var cellProps = getCellProps(cellConfig, props);
    var isDisabled = cellProps.isDisabled || cellProps.disabled || false;
    var focusedClassName = makeClassName(rowIndex, index);
    var className = cx(cellProps.originalClassName, focusedClassName, {
      'focus-disabled': isDisabled
    });
    return _objectSpread(_objectSpread({}, cellProps), {}, {
      className: className
    });
  };
};
var updateCellConfigs = _curry(function (columnConfig, index, cellConfig) {
  var component = cellConfig.getComponent();
  var shouldRender = cellConfig.getShouldRender();
  var updatedCellConfig = new CellConfig().setComponent(component).setShouldRender(shouldRender).setMapCellPropsToComponentProps(mapCellPropsToComponentProps({
    columnConfig: columnConfig,
    index: index,
    cellConfig: cellConfig
  }));
  return updatedCellConfig;
});
var addCellConfigToColumnConfig = _curry(function (columnConfig, cellConfig) {
  columnConfig.addCellConfig(cellConfig);
});
var updateMapCellPropsToComponentProps = function updateMapCellPropsToComponentProps() {
  var columnConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : DEFAULT_COLUMN_CONFIG;
  var index = arguments.length > 1 ? arguments[1] : undefined;
  var cellConfigs = columnConfig.getCellConfigs();
  var updatedCellConfigs = _map(cellConfigs, updateCellConfigs(columnConfig, index));
  columnConfig.resetCellConfigs();
  _forEach(updatedCellConfigs, addCellConfigToColumnConfig(columnConfig));
};
export var getRefListAndColumns = function getRefListAndColumns(props) {
  var columns = props.columns;
  var columnList = _forEach(columns, updateMapCellPropsToComponentProps);
  return columnList;
};
export var findParent = function findParent(element, className) {
  var elemClassName = element && element.getAttribute !== undefined && element.getAttribute('class');
  if (elemClassName && elemClassName.includes(className)) {
    return element;
  }
  return element.parentNode && findParent(element.parentNode, className);
};
export var isLastRowNotEmpty = function isLastRowNotEmpty() {
  var rows = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  var tableShortCutProps = arguments.length > 1 ? arguments[1] : undefined;
  var _tableShortCutProps$e = tableShortCutProps.excludedKeysForEmptyRowCheck,
    excludedKeysForEmptyRowCheck = _tableShortCutProps$e === void 0 ? EMPTY_ARRAY : _tableShortCutProps$e;
  var lastRow = _last(rows);
  var lastRowWithoutExcludedKeys = _omit(lastRow, excludedKeysForEmptyRowCheck);
  return isRowNotEmpty(lastRowWithoutExcludedKeys);
};
export var getDefaultColumnIdIndex = function getDefaultColumnIdIndex(columns, defaultColumnIdForEnter) {
  var columnIds = _map(columns, 'id');
  var defaultColumnIdIndex = defaultColumnIdForEnter ? _indexOf(columnIds, defaultColumnIdForEnter) : 0;
  return defaultColumnIdIndex;
};
export var getInput = function getInput(rowIndex, columnIndex, parent) {
  var rowCell = parent.getElementsByClassName("focusClass-indexed-".concat(rowIndex, ".").concat(columnIndex, "-indexed-"))[0];
  if (rowCell && rowCell instanceof Node) {
    var input = rowCell.querySelector('input');
    return input;
  }
  return undefined;
};
var getEmptyFocusableColumnIndexInRow = function getEmptyFocusableColumnIndexInRow(_ref2) {
  var startingColumnIndex = _ref2.startingColumnIndex,
    endingColumnIndex = _ref2.endingColumnIndex,
    columns = _ref2.columns,
    rowValue = _ref2.rowValue,
    currentRowIndex = _ref2.currentRowIndex,
    parent = _ref2.parent;
  for (var columnIndex = startingColumnIndex; columnIndex <= endingColumnIndex; columnIndex += 1) {
    var currentColumn = (columns === null || columns === void 0 ? void 0 : columns[columnIndex]) || EMPTY_OBJECT;
    var currentColumnId = currentColumn.id,
      getValueForKeyboardShortcuts = currentColumn.getValueForKeyboardShortcuts;
    var currentValue = rowValue === null || rowValue === void 0 ? void 0 : rowValue[currentColumnId];
    var columnValue = getValueForKeyboardShortcuts === null || getValueForKeyboardShortcuts === void 0 ? void 0 : getValueForKeyboardShortcuts(currentValue);
    var input = getInput(currentRowIndex, columnIndex, parent);
    if (input && (_isNil(columnValue) || isStringEmpty(columnValue))) return columnIndex;
  }
  return undefined;
};
var getNextFocusableFieldBeforeDefaultColumnId = function getNextFocusableFieldBeforeDefaultColumnId(_ref3) {
  var value = _ref3.value,
    currentRow = _ref3.currentRow,
    currentColumn = _ref3.currentColumn,
    defaultColumnIdIndex = _ref3.defaultColumnIdIndex,
    columns = _ref3.columns,
    parent = _ref3.parent;
  var currentRowValue = value === null || value === void 0 ? void 0 : value[currentRow];
  var focusableColumnIndex = getEmptyFocusableColumnIndexInRow({
    startingColumnIndex: currentColumn + 1,
    endingColumnIndex: defaultColumnIdIndex,
    columns: columns,
    rowValue: currentRowValue,
    currentRowIndex: currentRow,
    parent: parent
  });
  if (!_isNil(focusableColumnIndex)) {
    return {
      row: currentRow,
      column: focusableColumnIndex
    };
  }
  return {
    row: currentRow,
    column: defaultColumnIdIndex
  };
};
var getNextFocusableFieldOnOrAfterDefaultColumnId = function getNextFocusableFieldOnOrAfterDefaultColumnId(_ref4) {
  var value = _ref4.value,
    currentRow = _ref4.currentRow,
    currentColumn = _ref4.currentColumn,
    defaultColumnIdIndex = _ref4.defaultColumnIdIndex,
    columns = _ref4.columns,
    parent = _ref4.parent;
  var focusableColumnInCurrentRow = getEmptyFocusableColumnIndexInRow({
    startingColumnIndex: currentColumn + 1,
    endingColumnIndex: _size(columns) - 1,
    columns: columns,
    rowValue: value === null || value === void 0 ? void 0 : value[currentRow],
    currentRowIndex: currentRow,
    parent: parent
  });
  if (!_isNil(focusableColumnInCurrentRow)) {
    return {
      row: currentRow,
      column: focusableColumnInCurrentRow
    };
  }
  var focusableColumnInNextRow = getEmptyFocusableColumnIndexInRow({
    startingColumnIndex: 0,
    endingColumnIndex: defaultColumnIdIndex,
    columns: columns,
    rowValue: value === null || value === void 0 ? void 0 : value[currentRow + 1],
    currentRowIndex: currentRow + 1,
    parent: parent
  });
  if (!_isNil(focusableColumnInNextRow)) {
    return {
      row: currentRow + 1,
      column: focusableColumnInNextRow
    };
  }
  return {
    row: currentRow + 1,
    column: defaultColumnIdIndex
  };
};
export var getNextFocusableFieldOnEnter = function getNextFocusableFieldOnEnter(_ref5) {
  var defaultColumnIdIndex = _ref5.defaultColumnIdIndex,
    currentRow = _ref5.currentRow,
    currentColumn = _ref5.currentColumn,
    value = _ref5.value,
    columns = _ref5.columns,
    parent = _ref5.parent;
  var isCurrentColumnBeforeDefaultColumnId = currentColumn < defaultColumnIdIndex;
  if (isCurrentColumnBeforeDefaultColumnId) {
    var _nextFocusableField = getNextFocusableFieldBeforeDefaultColumnId({
      value: value,
      currentRow: currentRow,
      currentColumn: currentColumn,
      defaultColumnIdIndex: defaultColumnIdIndex,
      columns: columns,
      parent: parent
    });
    return _nextFocusableField;
  }
  var nextFocusableField = getNextFocusableFieldOnOrAfterDefaultColumnId({
    value: value,
    currentRow: currentRow,
    currentColumn: currentColumn,
    defaultColumnIdIndex: defaultColumnIdIndex,
    columns: columns,
    parent: parent
  });
  return nextFocusableField;
};